// Composable/mixin-style helpers for permission checks (additive only)
import { hasAnyModulePermission, normalizeModuleName } from './routePermission'

const getUserData = () => {
  try {
    return JSON.parse(localStorage.getItem('userData') || '{}');
  } catch (e) {
    return {};
  }
};

const getUserRights = () => {
  try {
    return JSON.parse(localStorage.getItem('UserRights') || '[]');
  } catch (e) {
    return [];
  }
};

const findRightsByModule = (moduleName) => {
  if (!moduleName) return null;
  const rights = getUserRights();
  const normalizedName = normalizeModuleName(moduleName);
  const normalizedNameLower = (moduleName || '').toLowerCase().trim();
  
  // Try exact match first (case-insensitive, trimmed)
  let found = rights.find(r => {
    const modName = (r.modulename || '').toLowerCase().trim();
    return modName === normalizedNameLower;
  });
  if (found) return found;
  
  // Try normalized match (handles spaces, hyphens, etc.)
  // e.g., "Site Visit" -> "sitevisit" matches "sitevisit"
  found = rights.find(r => {
    const modName = normalizeModuleName(r.modulename || '');
    return modName === normalizedName;
  });
  if (found) return found;
  
  // Try partial match with normalized names
  found = rights.find(r => {
    const modName = normalizeModuleName(r.modulename || '');
    return modName.includes(normalizedName) || normalizedName.includes(modName);
  });
  if (found) return found;
  
  // Try partial match with original names (case-insensitive)
  found = rights.find(r => {
    const modName = (r.modulename || '').toLowerCase().trim();
    return modName.includes(normalizedNameLower) || normalizedNameLower.includes(modName);
  });
  if (found) return found;
  
  // Route/module synonyms for better matching
  const synonyms = {
    courier: ['courier', 'request'],
    request: ['courier', 'request'],
    sitevisit: ['site visit', 'sitevisit', 'site-visit'],
    'site visit': ['site visit', 'sitevisit', 'site-visit'],
    'site-visit': ['site visit', 'sitevisit', 'site-visit'],
  };
  
  // Check synonyms using normalized name
  const syns = synonyms[normalizedName] || synonyms[normalizedNameLower];
  if (syns) {
    for (const syn of syns) {
      const normalizedSyn = normalizeModuleName(syn);
      found = rights.find(r => {
        const modName = normalizeModuleName(r.modulename || '');
        return modName === normalizedSyn || modName.includes(normalizedSyn) || normalizedSyn.includes(modName);
      });
      if (found) return found;
    }
  }
  
  // Final fallback: check if any module name contains the search term or vice versa (normalized)
  found = rights.find(r => {
    const modName = normalizeModuleName(r.modulename || '');
    // Check if either contains the other (handles variations like "HR Courier" vs "Courier")
    return modName.includes(normalizedName) || normalizedName.includes(modName);
  });
  
  return found || null;
};

const isAdmin = () => {
  const user = getUserData();
  return user && user.role === 'admin';
};

export const useRoutePermissions = () => {
  const canAdd = (moduleName) => {
    if (isAdmin()) return true;
    const rights = findRightsByModule(moduleName);
    return rights ? rights.add === 1 : false;
  };

  const canEdit = (moduleName) => {
    if (isAdmin()) return true;
    const rights = findRightsByModule(moduleName);
    return rights ? rights.edit === 1 : false;
  };

  const canView = (moduleName) => {
    if (isAdmin()) return true;
    const rights = findRightsByModule(moduleName);
    return rights ? rights.view === 1 : false;
  };

  const canDelete = (moduleName) => {
    if (isAdmin()) return true;
    const rights = findRightsByModule(moduleName);
    return rights ? rights.delete === 1 : false;
  };

  // Listing allowed if any permission exists
  const canList = (moduleName) => {
    if (isAdmin()) return true;
    const rights = findRightsByModule(moduleName);
    return hasAnyModulePermission(rights || {});
  };

  const hasModuleAccess = (moduleName) => canList(moduleName);

  const canPdf = (moduleName) => {
    if (isAdmin()) return true;
    const rights = findRightsByModule(moduleName);
    return rights ? rights.pdf === 1 : false;
  };

  const canExcel = (moduleName) => {
    if (isAdmin()) return true;
    const rights = findRightsByModule(moduleName);
    return rights ? rights.excel === 1 : false;
  };

  return {
    canAdd,
    canEdit,
    canView,
    canDelete,
    canList,
    hasModuleAccess,
    canPdf,
    canExcel,
  };
};

export default useRoutePermissions;

